<ui-confirmation-dialog
    header="import.reviewStep.removeModalHeader"
    primaryButtonLabel="delete"
    primaryButtonType="danger"
    secondaryButtonLabel="cancel"
    #removeModal
>
    <p>{{ 'import.reviewStep.removeModalHeader' | translate: { email: emailToBeRemoved } }}</p>
</ui-confirmation-dialog>

<div class="fullscreen-controls">
    <div>
        <ui-button type="link" (click)="toggleFullscreen()">
            <mat-icon svgIcon="fullscreen"></mat-icon>
            <ng-container *ngIf="isFullscreenEnabled; then opened; else closed"></ng-container>
            <ng-template #closed>{{ 'import.reviewStep.openFullscreen' | translate }}</ng-template>
            <ng-template #opened>{{ 'import.reviewStep.closeFullscreen' | translate }}</ng-template>
        </ui-button>
    </div>
</div>

<div class="app-grid">
    <div class="columns-8 flex align-items-end">
        <div class="legend">
            <div>
                <mat-icon class="success" svgIcon="complete"></mat-icon>
                {{ 'import.reviewStep.newEmployeesCount' | translate }}&nbsp;&nbsp;
                <span>{{ importerService.getRowCountByValidationState(ValidationState.new) }}</span>
            </div>
            <div>
                <mat-icon class="update" svgIcon="refresh"></mat-icon>
                {{ 'import.reviewStep.updateEmployeesCount' | translate }}&nbsp;&nbsp;
                <span>{{ importerService.getRowCountByValidationState(ValidationState.update) }}</span>
            </div>
            <div>
                <mat-icon class="warning" svgIcon="error"></mat-icon>
                {{ 'import.reviewStep.importErrorsCount' | translate }}&nbsp;&nbsp;
                <span>{{ importerService.getErrorCount() }}</span>
            </div>
        </div>
    </div>
    <div class="columns-4 flex col justify-end align-items-end">
        <app-error-navigator
            class="margin-bottom-1"
            [errorSelectors]="reviewErrorService.errorSelectors"
            (beforeChangeInErrorFocus)="handleBeforeErrorChange($event)"
            (nextErrorClicked)="handleNavigationToNextError()"
            (previousErrorClicked)="handleNavigationToPreviousError()"
            #errorNavigator
        ></app-error-navigator>
        <app-pagination
            *ngIf="rowPagination.total >= rowPagination.limit"
            [total]="rowPagination.total"
            [limit]="rowPagination.limit"
            [current]="rowPagination.current"
            (pageChange)="changePage($event)"
        ></app-pagination>
    </div>
</div>

<form #form="ngForm" [class.hasPagination]="hasPagination">
    <div class="left-sentinel"></div>
    <table>
        <thead>
            <tr>
                <th class="status top-sticky left-sticky"></th>
                <th
                    *ngFor="let humiColumn of columnService.mappedColumns; let first = first"
                    class="top-sticky"
                    [class.left-sticky]="first"
                >
                    @if (humiColumn.translatableLabel) {
                        {{ humiColumn.translatableLabel | translate }}
                    } @else {
                        {{ humiColumn.label }}
                    }
                </th>
            </tr>
        </thead>

        <tbody>
            <ng-container *ngIf="shouldRenderEmployeeRows">
                <tr
                    *ngFor="let row of importerService.processedRows | take: rowPagination.limit : getPageUpperLimit()"
                    [formGroup]="row.formControls"
                    class="employee-row"
                >
                    <td class="status left-sticky">
                        <ng-container *ngIf="row.isRequesting; else status">
                            <mat-icon svgIcon="refresh" class="loading"></mat-icon>
                        </ng-container>

                        <ng-template #status>
                            <mat-icon
                                class="warning"
                                svgIcon="error"
                                *ngIf="rowHasError(row); else checkSuccess"
                            ></mat-icon>

                            <ng-template #checkSuccess>
                                <mat-icon
                                    *ngIf="row.states !== ValidationState.update; else update"
                                    class="success"
                                    svgIcon="complete"
                                ></mat-icon>

                                <ng-template #update>
                                    <mat-icon class="update" svgIcon="refresh"></mat-icon>
                                </ng-template>
                            </ng-template>
                        </ng-template>

                        <!-- Delete Button -->
                        <span class="delete" (click)="removeRow(row)">
                            <mat-icon svgIcon="trash"></mat-icon>
                        </span>
                    </td>

                    <td
                        *ngFor="let humiColumn of columnService.mappedColumns; let first = first"
                        [ngSwitch]="humiColumn.input"
                        [ngClass]="{
                            'left-sticky': first,
                            bold: first,
                            select: humiColumn.input === InputType.options,
                            error: !!row.errors && !!row.errors[humiColumn.csvColumn.index],
                        }"
                    >
                        <div class="tooltip" *ngIf="!!row.errors && !!row.errors[humiColumn.csvColumn.index]">
                            {{ row.errors[humiColumn.csvColumn.index] }}
                            <svg
                                class="arrow"
                                width="8"
                                height="12"
                                viewBox="0 0 8 12"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <rect
                                    width="6.52777"
                                    height="6.52777"
                                    transform="matrix(0.499032 -0.866583 0.499032 0.866583 0.790039 5.65674)"
                                />
                            </svg>
                        </div>
                        <!-- Email -->
                        <input
                            *ngSwitchCase="InputType.email"
                            type="email"
                            required
                            [formControlName]="'' + humiColumn.csvColumn.index"
                            [class]="'row-' + row.id + '-cell-' + humiColumn.csvColumn.index"
                            [attr.data-row-id]="row.id"
                            [attr.data-column-id]="humiColumn.csvColumn.index"
                            [attr.data-no-scroll-to]="humiColumn.serializeName === 'account.email'"
                            (focusin)="errorNavigator.handleInputFocus($event)"
                        />

                        <!-- Select -->
                        <select
                            *ngSwitchCase="InputType.options"
                            [required]="humiColumn.required"
                            [formControlName]="'' + humiColumn.csvColumn.index"
                            [class]="'row-' + row.id + '-cell-' + humiColumn.csvColumn.index"
                            [attr.data-row-id]="row.id"
                            [attr.data-column-id]="humiColumn.csvColumn.index"
                            (focusin)="errorNavigator.handleInputFocus($event)"
                        >
                            <option *ngFor="let option of humiColumn.options" [value]="option.value">
                                {{ option.label }}
                            </option>
                        </select>

                        <!-- Boolean -->
                        <select
                            *ngSwitchCase="InputType.boolean"
                            [required]="humiColumn.required"
                            [formControlName]="'' + humiColumn.csvColumn.index"
                            [class]="'row-' + row.id + '-cell-' + humiColumn.csvColumn.index"
                            [attr.data-row-id]="row.id"
                            [attr.data-column-id]="humiColumn.csvColumn.index"
                            (focusin)="errorNavigator.handleInputFocus($event)"
                        >
                            <option [value]="1">{{ 'yes' | translate }}</option>
                            <option [value]="0">{{ 'no' | translate }}</option>
                        </select>

                        <!-- Number -->
                        <input
                            *ngSwitchCase="InputType.integer"
                            type="number"
                            required
                            [formControlName]="'' + humiColumn.csvColumn.index"
                            [class]="'row-' + row.id + '-cell-' + humiColumn.csvColumn.index"
                            [attr.data-row-id]="row.id"
                            [attr.data-column-id]="humiColumn.csvColumn.index"
                            (focusin)="errorNavigator.handleInputFocus($event)"
                        />

                        <!-- Text Area / Long Text -->
                        <textarea
                            *ngSwitchCase="InputType.longText"
                            required
                            rows="1"
                            [formControlName]="'' + humiColumn.csvColumn.index"
                            [class]="'row-' + row.id + '-cell-' + humiColumn.csvColumn.index"
                            [attr.data-row-id]="row.id"
                            [attr.data-column-id]="humiColumn.csvColumn.index"
                            (focusin)="errorNavigator.handleInputFocus($event)"
                        >
                        </textarea>

                        <!-- Default -->
                        <input
                            *ngSwitchDefault
                            type="text"
                            [required]="humiColumn.required"
                            [formControlName]="'' + humiColumn.csvColumn.index"
                            [class]="'row-' + row.id + '-cell-' + humiColumn.csvColumn.index"
                            [attr.data-row-id]="row.id"
                            [attr.data-column-id]="humiColumn.csvColumn.index"
                            (focusin)="errorNavigator.handleInputFocus($event)"
                        />
                    </td>
                </tr>
            </ng-container>
        </tbody>
    </table>
</form>
